import { formatToDollar, SuretyAccountId, SuretyBondId } from "@inrev/common";
import { useAdminFetchAdminUsers } from "@portal/api/admin-user/admin";
import { SuretyBondPreview } from "@portal/api/bond/agent";
import { useFetchBondPreviews } from "@portal/api/previews";
import { SearchableSelectListInput } from "@portal/components/form/inputs/SearchableSelectListInput";
import { Modal } from "@portal/components/layout/Modal";
import { cn } from "@portal/components/lib/utils";
import { Avatar, Button, Spinner } from "@portal/components/primitives";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { useState } from "react";
import { formatEmpty, formatName } from "../../../../utils";

type BidBondSelectorProps = {
	onSelect: (bondPreview?: SuretyBondPreview) => void;
	selectedBondId?: SuretyBondId;
	buttonLabel: string;
	accountId?: SuretyAccountId;
};

export const BidBondSelector = ({
	onSelect,
	selectedBondId,
	buttonLabel,
	accountId,
}: BidBondSelectorProps) => {
	const { bondPreviews, bondPreviewsIsLoading } = useFetchBondPreviews();
	const [open, setOpen] = useState(false);
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();

	const filteredBidBonds = bondPreviews
		?.filter(
			(bond) =>
				bond.type === "bid" &&
				bond.status === "open" &&
				(accountId ? bond.accountId === accountId : true),
		)
		.map((bond) => ({
			value: bond.id,
			element: <BidBondSelectorListItem key={bond.id} {...bond} />,
			searchValues: [
				bond.number,
				bond.principalName,
				isMinimalUIEnabled ? bond.name : bond.description,
				bond.carrierDisplayName,
				bond.accountName,
				bond.amount.toString(),
				bond.obligeeName,
			].filter((v): v is string => v != null),
		}));

	if (bondPreviewsIsLoading || !bondPreviews || !filteredBidBonds) {
		return <Spinner size="lg" className="text-secondary" />;
	}

	return (
		<div>
			{selectedBondId ? (
				<div
					className="cursor-pointer border rounded-lg hover:border-gray-800"
					onClick={() => setOpen(true)}
				>
					<BidBondSelectorListItem
						compact
						{...bondPreviews.find((bond) => bond.id === selectedBondId)!}
					/>
				</div>
			) : (
				<Button onClick={() => setOpen(true)} color="primary" className="h-[46px] w-full">
					{buttonLabel}
				</Button>
			)}
			{open && (
				<Modal onClickOutside={() => setOpen(false)}>
					<div className="w-[100vw] md:w-[50vw] bg-white px-6 py-8 rounded-md">
						<SearchableSelectListInput
							value={selectedBondId}
							listItems={filteredBidBonds}
							searchPlaceholder="Search bid bonds..."
							onChange={(value) => {
								if (value) {
									const bond = bondPreviews.find((bond) => bond.id === value);
									if (bond) {
										onSelect(bond);
										setOpen(false);
									}
								} else {
									onSelect();
									setOpen(false);
								}
							}}
							onBlur={() => {}}
						/>
					</div>
				</Modal>
			)}
		</div>
	);
};

type BidBondSelectorListItemProps = Pick<
	SuretyBondPreview,
	| "accountName"
	| "number"
	| "principalName"
	| "underwriterUserId"
	| "description"
	| "name"
	| "carrierDisplayName"
	| "obligeeName"
	| "accountId"
	| "amount"
> & {
	showUnderwriterInline?: boolean;
	compact?: boolean;
};

export const BidBondSelectorListItem = ({
	number,
	principalName,
	underwriterUserId,
	description,
	name,
	carrierDisplayName,
	obligeeName,
	amount,
	showUnderwriterInline,
	compact,
}: BidBondSelectorListItemProps) => {
	const { adminUsers } = useAdminFetchAdminUsers();
	const underwriter = adminUsers?.find((user) => user.id === underwriterUserId);
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();

	if (!underwriter) return null;

	return (
		<div
			className={cn(
				"w-full items-center flex",
				compact ? "space-x-4 py-[12px] px-[16px]" : "space-x-6 py-[20px] px-[30px]",
			)}
		>
			<div
				className={cn("flex flex-col flex-1 min-w-0", compact ? "space-y-[8px]" : "space-y-[15px]")}
			>
				<div className="flex flex-col space-y-[3px]">
					<div className="flex font-medium text-primary space-x-[12px]">
						<BoldedText>{number}</BoldedText>
					</div>
					<div className="flex font-normal text-gray-700 text-md gap-1">
						<BoldedText>{formatToDollar(amount)}</BoldedText>
						Bid Bond for
						<BoldedText>{principalName}</BoldedText>
					</div>
					<div className="flex flex-col space-y-[3px] font-normal text-gray-700 text-sm">
						<div className="flex flex-row gap-2 min-w-0">
							<span className="break-words">
								<BoldedText>{isMinimalUIEnabled ? "Project Name: " : "Description: "}</BoldedText>
								{isMinimalUIEnabled ? formatEmpty(name) : description}
							</span>
						</div>
						<div className="flex flex-row gap-2">
							<BoldedText>Obligee: </BoldedText>
							{formatEmpty(obligeeName)}
						</div>
						<div className="flex flex-row gap-2">
							<BoldedText>Carrier: </BoldedText>
							{carrierDisplayName}
						</div>
						{showUnderwriterInline && (
							<div className="flex flex-row gap-2">
								<BoldedText>Underwriter: </BoldedText>
								{formatName({ firstName: underwriter.firstName, lastName: underwriter.lastName })}
							</div>
						)}
					</div>
				</div>
			</div>
			{!showUnderwriterInline && (
				<Avatar firstName={underwriter.firstName} lastName={underwriter.lastName} />
			)}
		</div>
	);
};

const BoldedText = ({ children }: { children: React.ReactNode }) => (
	<span className="font-medium text-primary w-fit">{children}</span>
);
